import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PARTY_HALL_AMENITY_OPTIONS,
  PARTY_HALL_EVENT_TYPE_OPTIONS,
  PARTY_HALL_TYPES,
  joinPartyHallListValue,
  splitPartyHallListValue,
} from './restaurantServiceConstants';

function PartyHallMultiSelect({
  label,
  options,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select…',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = useMemo(() => splitPartyHallListValue(value), [value]);

  const optionList = useMemo(() => {
    const preset = options.map((opt) => String(opt).trim()).filter(Boolean);
    const extras = selected.filter(
      (item) => !preset.some((opt) => opt.toLowerCase() === item.toLowerCase())
    );
    return [...preset, ...extras];
  }, [options, selected]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (item) => {
    const exists = selected.some((row) => row.toLowerCase() === item.toLowerCase());
    const next = exists
      ? selected.filter((row) => row.toLowerCase() !== item.toLowerCase())
      : [...selected, item];
    onChange(joinPartyHallListValue(next));
  };

  const summary =
    selected.length === 0
      ? placeholder
      : selected.length <= 2
        ? selected.join(', ')
        : `${selected.length} selected`;

  return (
    <div
      className={`party-hall-multi-select${open ? ' party-hall-multi-select--open' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`party-hall-multi-select-trigger${
          selected.length === 0 ? ' party-hall-multi-select-trigger--placeholder' : ''
        }`}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className="party-hall-multi-select-trigger-label">{summary}</span>
        <span className="party-hall-multi-select-chevron" aria-hidden="true" />
      </button>
      {open ? (
        <div className="party-hall-multi-select-panel" role="listbox" aria-label={label} aria-multiselectable="true">
          {optionList.map((item) => {
            const checked = selected.some((row) => row.toLowerCase() === item.toLowerCase());
            return (
              <label key={item} className="party-hall-multi-select-option">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(item)}
                  disabled={disabled}
                />
                <span>{item}</span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

const PartyHallServiceFields = ({ value, onChange, disabled = false }) => {
  const update = (patch) => onChange({ ...value, ...patch });

  return (
    <>
      <label className="restaurant-services-picker-field">
        <span className="restaurant-services-picker-field-label">Hall Name</span>
        <input
          type="text"
          value={value.hallName || ''}
          onChange={(e) => update({ hallName: e.target.value })}
          disabled={disabled}
          placeholder="Defaults to restaurant name"
        />
      </label>
      <label className="restaurant-services-picker-field">
        <span className="restaurant-services-picker-field-label">Hall Type</span>
        <select
          value={value.hallType || ''}
          onChange={(e) => update({ hallType: e.target.value })}
          disabled={disabled}
        >
          <option value="">Select hall type</option>
          {PARTY_HALL_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <label className="restaurant-services-picker-field">
        <span className="restaurant-services-picker-field-label">Base Price</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={value.basePrice || ''}
          onChange={(e) => update({ basePrice: e.target.value })}
          disabled={disabled}
          placeholder="Optional"
        />
      </label>
      <label className="restaurant-services-picker-field restaurant-services-picker-field--wide">
        <span className="restaurant-services-picker-field-label">Description</span>
        <input
          type="text"
          value={value.description || ''}
          onChange={(e) => update({ description: e.target.value })}
          disabled={disabled}
          placeholder="Optional description"
        />
      </label>
      <div className="restaurant-services-picker-field restaurant-services-picker-field--wide">
        <span className="restaurant-services-picker-field-label">Amenities</span>
        <PartyHallMultiSelect
          label="Amenities"
          options={PARTY_HALL_AMENITY_OPTIONS}
          value={value.amenities || ''}
          onChange={(amenities) => update({ amenities })}
          disabled={disabled}
          placeholder="Select amenities"
        />
      </div>
      <div className="restaurant-services-picker-field restaurant-services-picker-field--wide">
        <span className="restaurant-services-picker-field-label">Event Types</span>
        <PartyHallMultiSelect
          label="Event Types"
          options={PARTY_HALL_EVENT_TYPE_OPTIONS}
          value={value.eventTypes || ''}
          onChange={(eventTypes) => update({ eventTypes })}
          disabled={disabled}
          placeholder="Select event types"
        />
      </div>
    </>
  );
};

export default PartyHallServiceFields;
